import React, { createContext, useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import * as Sentry from '@sentry/browser'
import moment from 'moment'

import {
  calculateAvailabilities,
  confirmReserve,
  getReserve,
  getUserSchedulings,
  insertReserve,
  insertScheduling,
  reschedule
} from '../services/schedulingService'

import { getPrice, getServiceDurations } from '../services/priceService'
import { getPetsLocally, batchCreateOrEditPet, clearPetsLocally } from '../services/petProfileService'
import { getAllMyPets } from '../services/petService'
import { getServices } from '../services/serviceService'
import { getNames } from '../services/professionalService'
import { getPartnerById } from '../services/partnerService'

import { getCampaignsByPartnerAndService } from '../services/campaignService'

import {
  scrollTop,
  calcDiscountedPrice,
  PAGE_URL,
  splitServicesList,
  calculateTotalPrice
} from '../utils'
import MODAL from '../utils/modals'
import * as gtag from '../utils/gtag'
import {
  DEFAULT_ERROR,
  INITIAL_ACTIVE_STEP,
  INITIAL_CALENDAR_DATE,
  INITIAL_SCHEDULE_BODY,
  INITIAL_SCHEDULING_STATE,
  SCHEDULING_ERROR_MESSAGES,
  SCHEDULING_STEPS,
  SERVICE_PACKAGE_STEPS,
  PURCHASED_PACKAGE_SCHEDULING_STEPS
} from '../utils/schedulingConstants'
import { featureFlags } from '../utils/constants'

import { PetAndServicesContextProvider } from './petAndServicesContext'
import { ProfessionalContextProvider } from './professionalContext'
import { LocationContextProvider } from './locationContext'
import { DateTimeContextProvider } from './dateTimeContext'
import { getActiveCoupon } from 'src/services/couponService'
import useReducerWithCallback from './useReducerWithCallback'
import { usePaymentContext } from './paymentContext'
import { getValidCoupon } from 'src/services/schedulingCouponService'
import { getBestPromotion } from 'src/services/promotionService'
import { getPurchasedPackageById } from 'src/services/purchasedPackagesService'
import {
  buildSelectedPurchasedPackage,
  filterAdditionalServiceIdsByPackageAvailability,
  parseSchedulingAdditionalQuantity,
  resolvePackageServiceIndex,
  sumAdditionalServiceQuantitiesById
} from 'src/utils/purchasedPackage'
import { shouldResetScheduling } from 'src/utils/scheduling'

const serviceScheduleContext = createContext()

// funções
export const actionTypes = {
  UPDATE_STATE: 'UPDATE_STATE',
  RESET_STATE: 'RESET_STATE'
}

// atualiza o estado
const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.UPDATE_STATE:
      return { ...state, ...action.payload }
    case actionTypes.RESET_STATE:
      return INITIAL_SCHEDULING_STATE
    default:
      return state
  }
}

// salva o estado no localStorage
const saveStateToLocalStorage = (state) => {
  try {
    const serializedState = JSON.stringify(state)
    window.localStorage.setItem('schedulingState', serializedState) // eslint-disable-line
  } catch (err) {
    console.error('Erro ao salvar no localStorage:', err)
  }
}

// carrega o estado do localStorage
const loadStateFromLocalStorage = () => {
  try {
    if (shouldResetScheduling()) {
      window.localStorage.removeItem('schedulingState')
      window.localStorage.removeItem('paymentState')
      window.localStorage.removeItem('category')
      return undefined
    }
    const serializedState = window.localStorage.getItem('schedulingState') // eslint-disable-line
    return serializedState ? JSON.parse(serializedState) : undefined
  } catch (err) {
    console.error('Erro ao carregar do localStorage:', err)
    return undefined
  }
}

// contexto de estado
export function ServiceScheduleContextProvider ({ children }) {
  const router = useRouter()
  const loadedState = loadStateFromLocalStorage() || INITIAL_SCHEDULING_STATE
  const initialState = featureFlags.enablePackagePurchase
    ? loadedState : {
      ...loadedState,
      scheduleBody: {
        ...loadedState.scheduleBody,
        isServicePackage: false,
        package_id: undefined
      }
    }
  const { functions: { updateSchedulings, updatePackageData } } = usePaymentContext()
  const [state, dispatch] = useReducerWithCallback(reducer, initialState)
  const handleShouldInvertSteps = () => {
    const { scheduleBody, formList, petsData } = state

    const selectedPet = petsData.find(pet => pet.id === scheduleBody.pet_id)
    const prevSchedule = formList[formList.length - 1]
    const stateToRender = !selectedPet && prevSchedule ? prevSchedule.data : state

    return stateToRender.scheduleBody.samePetDate || stateToRender.invertStep
  }

  const isAdditionalServicesEqual = (prevScheduling, currentScheduling) => {
    const prevServices = prevScheduling.additionalServices?.map(({ id }) => id).sort() || []
    const currentServices = currentScheduling.additionalServices?.map(({ id }) => id).sort() || []

    return (
      prevServices.length === currentServices.length &&
      prevServices.every((id, index) => id === currentServices[index])
    )
  }

  const hasSchedulingChanged = (updatedState = false, ignoreDate = false) => {
    const { scheduleBody, schedulingData } = updatedState || state
    const additionalServicesEqual = isAdditionalServicesEqual(schedulingData, scheduleBody)

    return (
      schedulingData &&
      (
        scheduleBody.pet_id !== schedulingData.pet?.id ||
        scheduleBody.service_id !== schedulingData.service?.id ||
        scheduleBody.partner_id !== schedulingData.partner?.id ||
        scheduleBody.professional_id !== schedulingData.professional?.id ||
        state?.schedulingCoupon?.id !== schedulingData?.schedulingCoupon?.id ||
        (!ignoreDate && (scheduleBody.selectDay !== schedulingData.selectDay || scheduleBody.selectTime !== schedulingData.selectTime)) ||
        !additionalServicesEqual
      )
    )
  }

  const schedulingItemErrorCallback = (error) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        schedulingItemError: error
      }
    })
  }

  // verifica se a data já está registrada para outro profissional no caso de multiplos agendamentos
  const checkReservedDate = ({ professional, selectDay, selectTime, serviceDurations = state.serviceDurations }) => {
    const { formList, pricesData } = state

    // Se não há agendamentos no formList, não há conflito
    if (!formList || formList.length === 0) {
      return true
    }

    // Prioriza a duração resolvida para o profissional (config própria ou padrão da loja).
    // pricesData vem da listagem de lojas, cujo service_time é o maior entre todos os profissionais.
    // Se pricesData estiver vazio, usa 60 minutos como duração padrão para garantir segurança
    const currentServiceDuration = serviceDurations?.[professional] || pricesData.reduce((total, service) => {
      return total + (service.service_time || 60)
    }, 0) || 60

    // Dia selecionado + horário de início do serviço em agendamento
    const selectedStartDate = moment(selectDay + ' ' + selectTime, 'YYYY-MM-DD HH:mm')
    // Dia selecionado + horário de término do serviço em agendamento
    const selectedEndDate = selectedStartDate.clone().add(currentServiceDuration, 'minutes')

    return !formList.some(form => {
      const { body, data } = form
      const { selectDay: existingDate, selectTime: existingTime, professional_id: existingProfId } = body
      const { pricesData: existingPrices, serviceDurations: existingServiceDurations } = data

      // Só verifica conflito se for o MESMO profissional (comparação com String para evitar problemas de tipo) e mesma data
      if (String(existingProfId) !== String(professional) || existingDate !== selectDay) {
        return false
      }

      // Calcula duração do agendamento existente (soma todos os serviços)
      // Usa 60 minutos como padrão se não houver dados de preço
      const existingDuration = existingServiceDurations?.[existingProfId] || existingPrices?.reduce((total, service) => {
        return total + (service.service_time)
      }, 0)

      // Data de início e fim do serviço já agendado
      const serviceStart = moment(existingDate + ' ' + existingTime, 'YYYY-MM-DD HH:mm')
      const serviceEnd = serviceStart.clone().add(existingDuration, 'minutes')

      // Verifica se há sobreposição de horários para o mesmo profissional
      const hasOverlap = selectedStartDate.isBefore(serviceEnd) && selectedEndDate.isAfter(serviceStart)

      return hasOverlap
    })
  }

  // atualiza quando lojas terminaram de carregar
  const handleSetRescheduleDataLoaded = () => {
    if (!state.rescheduleDataLoaded) {
      dispatch({ type: actionTypes.UPDATE_STATE, payload: { rescheduleDataLoaded: true } })
    }
  }

  // fecha modal de erro
  const handleWarningModalClose = () => {
    dispatch({ type: actionTypes.UPDATE_STATE, payload: { modal: undefined } })
  }

  // retorna modal de erro
  const showSchedulingFailureModal = (partnerId) => {
    const {
      partnersData,
      scheduleBody
    } = state

    const { phone } = {
      ...partnersData.find(({ id }) => id === partnerId)
    }

    return {
      modal: MODAL.SCHEDULE_ERROR_GENERIC(handleWarningModalClose(), phone),
      scheduleBody: {
        ...scheduleBody
      },
      pricesData: [],
      serviceDurations: {}
    }
  }

  const showCouponLimitReachedModal = (router, errorMessage) => {
    const { activeStep } = state
    const onConfirm = () => {
      handleWarningModalClose()
      router.push({
        pathname: `${PAGE_URL.SCHEDULING_REVIEW}`,
        query: { ...router.query, showDiary: false }
      })
    }

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.COUPON_LIMIT_REACHED_ERROR(
          onConfirm,
          errorMessage
        ),
        isHowToPay: false,
        activeStep: (activeStep - 1),
        review: true
      }
    })
  }

  // retorna campanhas por mês
  const handleFetchCampaigns = async (calendarDate, partnerId, serviceId) => {
    const campaignList = await getCampaignsByPartnerAndService(partnerId, serviceId, calendarDate)

    return campaignList
  }

  const addCouponToSchedule = async ({ context, coupon, callback }) => {
    context.showLoader()

    const { pricesData, activeCampaign, formList } = state
    const price = calculateTotalPrice(pricesData)

    const params = {
      code: coupon,
      purchaseValue: price,
      user: window.localStorage.getItem('userId'),
      partnerId: state.scheduleBody.partner_id,
      serviceId: state.scheduleBody.service_id,
      date: state.scheduleBody.selectDay
    }

    const schedulingCoupon = await getValidCoupon(params)

    if (schedulingCoupon?.success) {
      if (activeCampaign) {
        const bestDiscount = await getBestPromotion(activeCampaign.id, schedulingCoupon.data.id, price)

        dispatch({
          type: actionTypes.UPDATE_STATE,
          payload: {
            bestDiscount
          }
        })

        if (bestDiscount.promotion === 'campaign') {
          callback()
          return context.hideLoader()
        }
      }

      if (formList.length) {
        const couponId = schedulingCoupon.data.id
        let isCouponAlreadyUsed = false
        let couponIsLimitReached = false

        formList.some(({ scheduling }) => {
          const coupon = scheduling?.schedulingCoupon
          if (!coupon || coupon.id !== couponId) return false

          if (coupon.uniquePerClient) {
            isCouponAlreadyUsed = true
            return true
          }

          const maxUsage = coupon.maxUsage
          const usageCount = coupon.usageCount + 1
          if (usageCount >= maxUsage) {
            couponIsLimitReached = true
            return true
          }

          return false
        })

        if (isCouponAlreadyUsed) {
          // eslint-disable-next-line standard/no-callback-literal
          callback(['Cupom já utilizado'])
          context.hideLoader()
          return
        }

        if (couponIsLimitReached) {
          // eslint-disable-next-line standard/no-callback-literal
          callback(['Cupom esgotado'])
          context.hideLoader()
          return
        }
      }

      dispatch({
        type: actionTypes.UPDATE_STATE,
        payload: {
          schedulingCoupon: schedulingCoupon.data,
          bestDiscount: undefined
        }
      })
      callback()
      context.hideLoader()
      return
    }
    callback(schedulingCoupon.errors)
    context.hideLoader()
  }

  const removeCouponFromSchedule = () => {
    const { campaigns, scheduleBody: { selectDay, partner_id: partnerId, service_id: serviceId } } = state

    const activeCampaign = campaigns?.find((campaign) => (moment(selectDay).isBetween(
      moment(campaign.start),
      moment(campaign.end),
      null,
      '[]'
    )) && campaign.status === 'ACTIVE' &&
      campaign.partners.includes(partnerId) && campaign.services.includes(serviceId))

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        activeCampaign: activeCampaign,
        schedulingCoupon: undefined
      }
    })
  }

  const handleFetchCoupon = async () => await getActiveCoupon()

  const handleEditPetScheduling = (selectedForm, step = 1) => {
    const { formList, havePayment } = state
    const { body, data } = selectedForm
    const updatedFormList = formList.filter(
      (form) => form.body.pet_id !== body.pet_id
    )
    const updatedProfessionalData = data.professionalData.filter(
      ({ enabledPayment }) => (havePayment && enabledPayment === havePayment) || !havePayment || formList.length === 1
    )

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...data,
        professionalData: updatedProfessionalData,
        formList: updatedFormList,
        review: true,
        isHowToPay: false,
        activeStep: step,
        schedulingItemError: undefined
      }
    })
    scrollTop('auto')
  }

  // fecha modal de agendamento duplicado
  const handleCloseWarningDuplicatedScheduling = (router, schedulingIndexError) => {
    const schedulings = [...state.formList.map(formList => formList), state.scheduleBody]
    const schedulingLength = schedulings.length - 1

    if (schedulingLength !== schedulingIndexError) {
      const schedulingToEdit = schedulings[schedulingIndexError]
      return handleEditMultipleScheduling(schedulingToEdit)
    }

    const stepToRedirect = state.scheduleBody.isSchedulingFromPackage ? PURCHASED_PACKAGE_SCHEDULING_STEPS.DATE_AND_TIME : state.invertStep ? SCHEDULING_STEPS.PROFESSIONAL : SCHEDULING_STEPS.DATE_AND_TIME
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...editSteps()[stepToRedirect],
        modal: undefined,
        review: false,
        isHowToPay: false,
        scheduleBody: {
          ...editSteps()[stepToRedirect].scheduleBody,
          selectDay: undefined,
          selectTime: undefined
        }
      }
    })

    const { path } = steps.find(item => item.id === stepToRedirect)

    const { category } = router.query

    if (!category) {
      router.push(`/agendamentos${path}`)
      return
    }

    router.push(`/agendamentos${path}/?category=${category}`)
  }

  // exibe modal de agendamento duplicado
  const showSchedulingDuplicateModal = ({ errorMessage, callback, router, schedulingIndexError }) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.DUPLICATED_SCHEDULING_WARNING(() => handleCloseWarningDuplicatedScheduling(router, schedulingIndexError), errorMessage)
      }
    })

    if (callback) callback()
  }

  const handleScheduleEventSend = async ({ scheduling, isReactNative }) => {
    try {
      const { serviceData } = state
      const schedulingsCreated = [scheduling, ...scheduling.nestedSchedulings || []]

      const promises = schedulingsCreated.map(async schedulingCreated => {
        const serviceCategory = serviceData.find(service => service.value === schedulingCreated.service.id)?.category

        const purchaseDetails = {
          scheduling: schedulingCreated,
          serviceCategory,
          isReactNative: isReactNative
        }

        gtag.scheduleEvent(purchaseDetails)
      })

      await Promise.all(promises)
    } catch (err) {
      Sentry.captureException(err)
    }
  }

  function canGetCampaigns (scheduleBody) {
    return !scheduleBody?.isSchedulingFromPackage && !scheduleBody?.isServicePackage
  }

  function filterUnavailableWeekdaysFromPackage (availableSlotsByProfessional, availabilityParams) {
    const { selectedPurchasedPackage } = state

    const year = availabilityParams.year
    const month = availabilityParams.month
    const allowedPackageWeekdays = selectedPurchasedPackage.service.packageWeekdays

    const weekdayMap = availableSlotsByProfessional.reduce((acc, day) => {
      acc[day] = moment({ year, month: month - 1, day }).day()
      return acc
    }, {})
    const filteredDays = availableSlotsByProfessional.filter(day =>
      allowedPackageWeekdays.includes(weekdayMap[day])
    )

    return filteredDays
  }

  function filterDatesByPackageExpiry (availableDates, year, month) {
    const { selectedPurchasedPackage, scheduleBody } = state

    if (!scheduleBody.isSchedulingFromPackage || !selectedPurchasedPackage?.expiresAt) {
      return availableDates
    }

    const expiresAt = moment(selectedPurchasedPackage.expiresAt)

    return availableDates.filter(day => {
      const date = moment({ year, month: month - 1, day })
      return date.isSameOrBefore(expiresAt, 'day')
    })
  }

  // busca a duração do serviço por profissional (config própria ou padrão da loja)
  // em caso de falha retorna vazio para que checkReservedDate use a duração de pricesData
  const fetchServiceDurations = async ({ partnerId, breedId, services, professionals }) => {
    if (!professionals.length) return {}

    try {
      const durations = await getServiceDurations({ partnerId, breedId, services, professionals })
      return durations || {}
    } catch (err) {
      console.error(err)
      return {}
    }
  }

  // busca disponibilidade de profissionais por loja, também busca dias disponiveis, horas e campanhas
  const findPartnerScheduleAndCampaignAviability = async (partner, day = undefined, selectedForm = false) => {
    const {
      scheduleBody,
      error,
      calendarMonth,
      calendarYear,
      partnersData,
      professionalData,
      schedulingData,
      formList,
      timeData,
      selectedPet,
      petsData,
      selectedPurchasedPackage
    } = state

    const {
      breed: { id: breedId },
      temperament
    } = selectedPet

    const {
      service_id: serviceId,
      professional_id: professionalId,
      additional_services: additional,
      isServicePackage
    } = scheduleBody

    const additionalServices = additional?.map(additional => additional.id) || []
    const newError = Object.assign({}, error)

    // usa dados de multiplo agendamento para selecionar dia e hora
    const partnerId = selectedForm ? selectedForm.body.partner_id : partner
    const selectDay = selectedForm ? selectedForm.body.selectDay : day
    const selectTime = selectedForm ? selectedForm.body.selectTime : undefined
    const partnerDataList = selectedForm ? selectedForm.data.partnersData : partnersData
    const selectedPetSchedule = selectedForm
      ? {
        id: selectedForm.body.pet_id,
        name: selectedForm.scheduling.petName
      }
      : undefined

    newError.partner_id = partnerId ? undefined : DEFAULT_ERROR.partner_id

    let newState = {
      ...state,
      availableDates: [],
      timeData: timeData || [],
      error: {
        ...error,
        partner_id: partnerId ? undefined : DEFAULT_ERROR.partner_id,
        ...(selectedForm && {
          selectDay: selectDay ? undefined : DEFAULT_ERROR.selectDay,
          selectTime: selectTime
            ? undefined
            : DEFAULT_ERROR.selectTime
        })
      },
      // Se já foi adicionado um agendamento para um pet,
      // define se haverá pagamento ou não de acordo com o primeiro agendamento
      havePayment: formList.length ? formList[0].data.havePayment : false
    }

    try {
      const partnerSelected = partnersData.find(
        (partner) => partner.id === partnerId
      )

      const partnerEnablePayment = partnerSelected?.enabledPayment === true
      const partnerOpenSeller = partnerSelected?.openSeller === true

      const professionalSelected = professionalData.find(
        (professional) => professional.id === professionalId
      )
      const professionalEnablePayment = professionalSelected?.enabledPayment === true

      const unchanged = !hasSchedulingChanged(state) || professionalId === undefined

      const havePrevPayment = schedulingData.paidAt !== undefined && unchanged

      // Se já foi adicionado um agendamento para um pet, define se haverá pagamento ou não de acordo com o primeiro agendamento,
      // caso contrário, verifica se o pagamento está habilitado para a loja e profissional selecionados no agendamento atual
      const havePayment = formList.length
        ? formList[0].data.havePayment
        : partnerEnablePayment &&
        (professionalEnablePayment || partnerOpenSeller)

      newState.havePayment = havePayment

      // busca slots disponiveis (se a loja possui profissionais disponíveis)
      const availabilityParams = {
        partnerId,
        serviceId,
        additionalServices,
        breedId,
        temperament,
        ...(havePayment && { havePayment })
      }

      // Para pacotes, usa mês/ano atual. Para serviços normais, considera data selecionada
      if (isServicePackage) {
        availabilityParams.month = calendarMonth
        availabilityParams.year = calendarYear
      } else {
        availabilityParams.month = selectDay // Se é um agendamento para mais de 1 pet e foi selecionado a opção de mesma data/hora já agendado para um dos pets
          ? moment(selectDay, 'YYYY-MM-DD').month() + 1 // Seta o mês relativo à data já selecionada
          : calendarMonth
        availabilityParams.year = selectDay // Se é um agendamento para mais de 1 pet e foi selecionado a opção de mesma data/hora já agendado para um dos pets
          ? moment(selectDay, 'YYYY-MM-DD').year()
          : calendarYear
        if (havePrevPayment) {
          availabilityParams.paidAt = schedulingData.paidAt
        }
      }

      const availableSlots = await calculateAvailabilities(availabilityParams)
      newState.availableSlots = availableSlots

      if (availableSlots) {
        // Extrai IDs dos profissionais disponíveis
        let professionalIds = Object.keys(availableSlots).filter(
          (professionalId) => professionalId !== ''
        )

        const services = [...additionalServices, serviceId]

        // só há risco de sobreposição quando já existe outro pet no agendamento
        const shouldFetchDurations = !isServicePackage && formList.length > 0
        const serviceDurations = shouldFetchDurations
          ? await fetchServiceDurations({ partnerId, breedId, services, professionals: professionalIds })
          : {}
        newState.serviceDurations = serviceDurations

        // Para serviços normais com selectedForm, filtra profissionais por data/hora
        if (!isServicePackage && selectedForm && selectDay) {
          const day = moment(selectDay, 'YYYY-MM-DD').date()
          professionalIds = professionalIds.filter(
            (professional) =>
              availableSlots[professional][day] &&
              availableSlots[professional][day].includes(selectTime)
          )

          // Se existem agendamentos anteriores, filtra os profissionais considerando a duração dos serviços para evitar sobreposição
          if (formList.length > 0) {
            professionalIds = professionalIds.filter((professional) =>
              checkReservedDate({ professional, selectDay, selectTime, serviceDurations })
            )
          }
        }

        const professionalList = await getNames(professionalIds)

        const professionalData = professionalList
          .map(({ id, name, photo, enabledPayment, ratingAverage }) => ({
            id,
            name,
            photo,
            enabledPayment: enabledPayment || false,
            ratingAverage
          }))
          .filter(({ enabledPayment }) => {
            if (isServicePackage) {
              return (havePayment && enabledPayment === havePayment) || !havePayment
            }
            return !formList.length || (havePayment && enabledPayment === havePayment) || !havePayment
          })

        if (!professionalData.length) {
          const unavailablePetSchedule = petsData.find(
            (pet) => selectedPetSchedule && pet.id === selectedPetSchedule.id
          )

          if (unavailablePetSchedule) {
            const newError = {
              ...error,
              unavailabilityPets: [
                ...error.unavailabilityPets,
                {
                  ...unavailablePetSchedule
                }
              ]
            }
            newState.error = newError
          }
        }

        newState.professionalData = professionalData

        newState = {
          ...newState,
          scheduleBody: {
            ...newState.scheduleBody,
            partner_id: partnerId,
            ...(selectedForm && !isServicePackage && {
              selectDay,
              selectTime
            })
          },
          ...(selectedForm && !isServicePackage && {
            selectedPetSchedule,
            partnersData: partnerDataList,
            invertStep: true
          })
        }

        // No fluxo de múltiplos pets (selectedForm), sempre busca novos preços
        // pois os parâmetros podem variar: serviço diferente, raça diferente, adicionais diferentes
        // Cada pet pode ter preços distintos mesmo para o mesmo serviço (ex: porte/raça)
        const shouldFetchNewPrices = selectedForm || !partnerSelected?.prices?.length

        // se tem profissionais disponíveis atualiza preços do serviço
        const price = shouldFetchNewPrices ? await getPrice({ partnerId, breedId, services }) : partnerSelected?.prices

        if (!price.length) {
          throw new Error('Nenhum valor foi encontrado para esse serviço.')
        }

        const pricesData = price
          ? price.map((priceData) => {
            return {
              service: priceData.service.name,
              price: priceData.price_value,
              service_time: priceData.service_time,
              additional: priceData.service.additional,
              package: priceData?.service?.package,
              packageDiscount: priceData?.service?.packageDiscount,
              packageQuantity: priceData?.service?.packageQuantity,
              id: priceData.service.id
            }
          })
          : []

        newState.pricesData = pricesData

        // Para serviços normais, busca campanhas e datas disponíveis
        if (!isServicePackage) {
          // No fluxo de múltiplos pets, atualiza os partnersData com os novos preços
          // do parceiro selecionado para refletir os valores corretos do pet atual
          if (selectedForm && newState.partnersData) {
            newState.partnersData = newState.partnersData.map(partner =>
              partner.id === partnerId
                ? { ...partner, prices: price }
                : partner
            )
          }

          const { timeData, scheduleBody } = newState
          const professionalId = scheduleBody.professional_id || ''

          // Inicialização dos arrays de dias disponíveis
          const availableSlotsByProfessional = availableSlots[professionalId]
            ? Object.keys(availableSlots[professionalId]).map((day) => parseInt(day))
            : []

          const availableDatesFiltered = scheduleBody.isSchedulingFromPackage ? filterUnavailableWeekdaysFromPackage(availableSlotsByProfessional, availabilityParams) : availableSlotsByProfessional
          const availableDates = filterDatesByPackageExpiry(availableDatesFiltered, availabilityParams.year, availabilityParams.month)

          const calendarDate = {
            month: selectDay // Se é um agendamento para mais de 1 pet e foi selecionado a opção de mesma data/hora já agendado para um dos pets
              ? moment(selectDay, 'YYYY-MM-DD').month() + 1 // Seta o mês relativo à data já selecionada
              : calendarMonth,
            year: selectDay // Se é um agendamento para mais de 1 pet e foi selecionado a opção de mesma data/hora já agendado para um dos pets
              ? moment(selectDay, 'YYYY-MM-DD').year() // Seta o ano relativo à data já selecionada
              : calendarYear
          }

          // Busca campanhas disponíveis
          const campaignList = canGetCampaigns(scheduleBody) ? await handleFetchCampaigns(
            calendarDate,
            partnerId,
            serviceId
          ) : []

          // seta os dias disponiveis e as campanhas
          newState = {
            ...newState,
            availableDates,
            timeData: timeData || [],
            campaigns: campaignList || []
          }

          // caso tenha o dia selecionado já seta campanha ativa
          if (selectDay) {
            const activeCampaign = campaignList?.find((campaign) => (moment(selectDay).isBetween(
              moment(campaign.start),
              moment(campaign.end),
              null,
              '[]'
            )) && campaign.status === 'ACTIVE' &&
              campaign.partners.includes(partnerId) && campaign.services.includes(serviceId))

            newState.activeCampaign = activeCampaign
          }
        }
      } else {
        newState = {
          ...newState,
          ...showSchedulingFailureModal(partner),
          scheduleBody: {
            ...newState.scheduleBody,
            partner_id: selectedPurchasedPackage ? scheduleBody.partner_id : undefined
          }
        }

        handleSetRescheduleDataLoaded()
      }

      return newState
    } catch (err) {
      console.error(err)

      newState = {
        ...newState,
        ...showSchedulingFailureModal(partner),
        scheduleBody: {
          ...newState.scheduleBody,
          partner_id: selectedPurchasedPackage ? scheduleBody.partner_id : undefined
        }
      }

      handleSetRescheduleDataLoaded()

      return newState
    }
  }

  // adiciona mais um pet ao agendamento
  const handleAddPetToScheduling = async (discountedPrice, onGoTo) => {
    const {
      scheduleBody,
      professionalData,
      serviceData,
      additionalServiceData,
      petsData,
      partnersData,
      pricesData,
      schedulingCoupon,
      selectedPet,
      serviceDurations
    } = state

    // o primeiro pet é agendado sem formList, então sua duração é resolvida aqui
    // para que os próximos pets verifiquem sobreposição com o tempo do profissional
    const professionalId = scheduleBody.professional_id
    const breedId = selectedPet?.breed?.id
    const scheduledServiceDurations = professionalId && breedId && !serviceDurations?.[professionalId]
      ? await fetchServiceDurations({
        partnerId: scheduleBody.partner_id,
        breedId,
        services: [
          ...(scheduleBody.additional_services?.map((service) => service?.id ?? service) || []),
          scheduleBody.service_id
        ],
        professionals: [professionalId]
      })
      : serviceDurations

    const schedulingPrice = pricesData.reduce((prev, next) => {
      return Number(prev) + Number(next.price)
    }, 0)

    const additionalServicesNames = scheduleBody.additional_services.map(
      (service) =>
        additionalServiceData.find(
          (additionalServiceItem) => additionalServiceItem.value === (service?.id ?? service)
        )?.name
    )

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        formList: [
          ...state.formList,
          {
            body: {
              ...scheduleBody
            },
            services: pricesData.map((service) => {
              return {
                price: service.price,
                professionalName: professionalData.find(
                  (professional) =>
                    professional.id === scheduleBody.professional_id
                )?.name,
                partnerName: partnersData.find(
                  (partner) => partner.id === scheduleBody.partner_id
                )?.name,
                serviceName: service.service,
                additional: service.additional,
                petName: petsData.find((pet) => pet.id === scheduleBody.pet_id)
                  ?.name
              }
            }),
            scheduling: {
              price: schedulingPrice,
              discountedPrice,
              schedulingCoupon,
              professionalName: professionalData.find(
                (professional) => professional.id === scheduleBody.professional_id
              )?.name,
              partnerName: partnersData.find(
                (partner) => partner.id === scheduleBody.partner_id
              )?.name,
              serviceName: serviceData.find(
                (service) => service.value === scheduleBody.service_id
              )?.name,
              additionalServices: additionalServicesNames,
              petName: petsData.find((pet) => pet.id === scheduleBody.pet_id)
                ?.name
            },
            data: {
              ...state,
              serviceDurations: scheduledServiceDurations
            }
          }
        ],
        scheduleBody: INITIAL_SCHEDULE_BODY,
        professionalData: [],
        pricesData: [],
        serviceDurations: {},
        timeData: [],
        availableDates: [],
        error: {
          ...DEFAULT_ERROR
        },
        haveError: false,
        usingGeolocation: false,
        modal: undefined,
        professionalProfile: undefined,
        calendarMonth: INITIAL_CALENDAR_DATE.calendarMonth,
        calendarYear: INITIAL_CALENDAR_DATE.calendarYear,
        obs: '',
        selectedPetSchedule: undefined,
        isAllAdditionalServicesSelected: false,
        review: false,
        isHowToPay: false,
        activeStep: INITIAL_ACTIVE_STEP,
        noPreference: false,
        changeStore: false,
        activeCampaign: undefined,
        invertStep: undefined,
        selectedPet: {},
        selectedPurchasedPackage: {},
        schedulingCoupon: undefined
      }
    }, () => {
      onGoTo(steps[0].path)
    })

    scrollTop('auto')
  }

  const handleEditMultipleScheduling = (form) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL
          .EDIT_MULTIPLE_SCHEDULING({
            onCancel: () => handleWarningModalClose(),
            onConfirm: () => {
              handleEditPetScheduling(form, 6)
              handleWarningModalClose()
            }
          })
      }
    })
  }

  // finaliza agendamento
  const handleConfirmScheduling = async ({ reschedule, context, router }) => {
    const { error, formList, scheduleBody } = state
    const hasBlockingErrors = !scheduleBody?.isSchedulingFromPackage &&
      (error.pet_id ||
        error.service_id ||
        error.professional_id ||
        error.selectDay ||
        error.selectTime)

    if (hasBlockingErrors && !formList.length) {
      dispatch({
        type: actionTypes.UPDATE_STATE,
        payload: {
          haveError: true
        }
      })
    } else {
      handleInsertSchedulingByPaymentType({ context, router })
    }
  }

  const handlePackagePurchase = async ({ context, router }) => {
    const { scheduleBody, selectedPet, serviceData } = state

    const additionalServiceIds = new Set(scheduleBody.additional_services?.map(additional => additional.id) || [])

    const additionalServices = state.pricesData?.filter(price =>
      additionalServiceIds.has(price.id)
    ).map(price => {
      const { service, ...priceWithoutService } = price
      const additionalService = scheduleBody.additional_services?.find(additional => additional.id === price.id)
      return {
        ...priceWithoutService,
        name: service,
        quantity: additionalService?.quantity || 1
      }
    }) || []

    const packageData = {
      id: scheduleBody.package_id,
      name: serviceData.find(service => service.value === scheduleBody.service_id)?.name,
      description: 'Pacote de serviços selecionados',
      price: pricesInfo().totalPriceWithDiscount,
      originalPrice: pricesInfo().priceWithoutPackage,
      additionalServices,
      serviceId: scheduleBody.service_id,
      service: (() => {
        const service = state.pricesData?.find(price => price.additional === false)
        return service ? {
          ...service,
          name: service.service,
          packageDiscount: service.packageDiscount // Garante que packageDiscount seja incluído
        } : null
      })(),
      pet: selectedPet,
      partner: state.partnersData.find(partner => partner.id === scheduleBody.partner_id),
      professional: state.professionalData.find(professional => professional.id === scheduleBody.professional_id),
      obs: state.obs
    }

    updatePackageData(packageData)

    router.push({
      pathname: PAGE_URL.CHECKOUT_ADDRESS,
      query: router.query
    })
  }

  const handleInsertSchedulingByPaymentType = async ({ context, router }) => {
    handleWarningModalClose()

    const redirectToCheckout = (schedulings) => {
      const { nestedSchedulings, ...rest } = schedulings
      // em casos de reagendamento não existe nestedSchedulings
      const flattenedSchedulings = Array.isArray(nestedSchedulings)
        ? nestedSchedulings.map(scheduling => scheduling)
        : []

      const schedulingList = [{ ...rest }, ...flattenedSchedulings]
      updateSchedulings(schedulingList)

      router.push(`${PAGE_URL.CHECKOUT_ADDRESS}`)
    }

    const {
      scheduleBody,
      formList,
      obs,
      petsData,
      schedulingCoupon
    } = state

    const isReschedule = router.query.id && router.query.reschedule
    const { paymentType } = scheduleBody
    const isPaymentOnline = paymentType === 'online'

    try {
      context.showLoader()
      const allSchedulings = formList.map((form) => {
        const requestedPet = context.firstName
          ? { id: form.body.pet_id }
          : {
            ...petsData[form.body.pet_id],
            id: undefined,
            breed: petsData[form.body.pet_id].breed.id
          }
        return {
          partnerId: form.body.partner_id,
          serviceId: form.body.service_id,
          additionalServices: form.body.additional_services.map(additionalService => additionalService.id),
          professionalId: form.body.professional_id,
          date: `${form.body.selectDay} ${form.body.selectTime}`,
          obs: form.data.obs,
          pet: requestedPet,
          ...(form.scheduling?.schedulingCoupon ? { couponId: form.scheduling.schedulingCoupon?.id } : {})
        }
      })

      let requestBody = {}
      if (
        (scheduleBody.pet_id || scheduleBody.pet_id === 0) &&
        scheduleBody.service_id &&
        scheduleBody.partner_id &&
        scheduleBody.professional_id &&
        scheduleBody.selectDay &&
        scheduleBody.selectTime
      ) {
        const date = scheduleBody.selectDay
          .concat(' ')
          .concat(scheduleBody.selectTime)
        const professionalId = scheduleBody.professional_id
        requestBody = {
          partnerId: scheduleBody.partner_id,
          serviceId: scheduleBody.service_id,
          additionalServices: scheduleBody.additional_services.map(additionalService => additionalService.id),
          ...(scheduleBody.purchasedPackageId ? { purchasedPackageId: scheduleBody.purchasedPackageId } : {}),
          couponId: schedulingCoupon?.id,
          professionalId,
          date,
          obs
        }

        context.firstName
          ? (requestBody.pet = {
            id: scheduleBody.pet_id
          })
          : (requestBody.pet = {
            ...petsData[scheduleBody.pet_id],
            id: undefined,
            breed: petsData[scheduleBody.pet_id].breed.id
          })

        allSchedulings.push(requestBody)
      }

      const multi = allSchedulings.length > 1 ? '?multi=true' : ''

      const isReactNative = context.isReactNative

      // INSERE AGENDAMENTO CASO USUÁRIO ESTEJA LOGADO
      if (context.firstName) {
        let scheduling = {}
        if (isReschedule) {
          scheduling = await reschedule(router.query.id, { ...requestBody, paymentType })
          await handleScheduleEventSend({ scheduling, isReactNative })
          // Verifica se o pet, serviço, loja ou profissional foram alterados no reagendamento e está liberado o pagamento para o seller
          if (hasSchedulingChanged() && isPaymentOnline) {
            // Se o agendamento gerou um pedido de pagamento, redireciona pra o checkout de pagamento
            redirectToCheckout(scheduling)
          } else {
            // Se o agendamento não gera um novo pagamento, redireciona para a home
            router.push(`${PAGE_URL.SCHEDULING_SUMMARY}/${scheduling.id}`)
          }
        } else if (isPaymentOnline) {
          scheduling = await insertScheduling(allSchedulings, true)
          await handleScheduleEventSend({ scheduling, isReactNative })
          redirectToCheckout(scheduling)
        } else {
          scheduling = await insertScheduling(allSchedulings)
          await handleScheduleEventSend({ scheduling, isReactNative })
          router.push(
            `${PAGE_URL.SCHEDULING_SUMMARY}/${scheduling.id}${multi}`
          )
        }
      // INSERE RESERVA PARA USUÁRIO NÃO LOGADO
      } else {
        if (!window.sessionStorage.getItem('reserve')) {
          await insertReserve(allSchedulings, isPaymentOnline)
        }
        const onAuthenticate = async () => {
          context.showLoader()
          try {
            const allPets = petsData.map((pet) => ({
              ...pet,
              id: undefined,
              breed: pet.breed.id
            })) // pets não envolvidos no agendamento
            const { id: reserveId } = getReserve()

            if (!allPets.length) {
              throw new Error('Nenhum pet selecionado')
            }

            const petsId = await batchCreateOrEditPet(allPets)

            const scheduling = await confirmReserve(petsId)
            await handleScheduleEventSend({ scheduling, isReactNative })
            clearPetsLocally()

            if (isPaymentOnline) {
              window.localStorage.setItem(
                'reservePay',
                JSON.stringify({
                  id: reserveId,
                  expiresAt: moment()
                    .add(process.env.SCHEDULING_TTL, 'minutes')
                    .format('YYYY-MM-DD HH:mm')
                })
              )
              redirectToCheckout(scheduling)
            } else {
              router.push(
                `${PAGE_URL.SCHEDULING_SUMMARY}/${reserveId}${multi}`
              )
            }
          } catch (err) {
            if (err.response && err.response.status === 422) {
              const callback = async () => {
                await context.logoutUser()
              }
              const errorMessage = err.response.data.message
              const schedulingIndexError = err.response.data.index
              showSchedulingDuplicateModal({
                errorMessage,
                callback,
                router,
                schedulingIndexError
              })
            } else {
              console.error(err)
              Sentry.captureException(err)
            }
          } finally {
            context.toggleModalSheet()
            context.hideLoader()
          }
        }
        context.toggleModalSheet(MODAL.SCHEDULE_RESERVED_LOGIN({ onCancel: context.toggleModalSheet, callback: onAuthenticate }))
      }
    } catch (err) {
      const errorData = err.response?.data
      const errorMessage = err.response?.data?.message
      const schedulingIndexError = err.response?.data?.index
      if (err.response?.status === 422) {
        return showSchedulingDuplicateModal({
          errorMessage,
          callback: () => schedulingItemErrorCallback(errorData),
          router,
          schedulingIndexError
        })
      }

      if (SCHEDULING_ERROR_MESSAGES.coupon_limit_reached === errorMessage) {
        return showCouponLimitReachedModal(router, errorMessage)
      }

      dispatch({ type: actionTypes.UPDATE_STATE, payload: showSchedulingFailureModal(scheduleBody?.partner_id) })
    } finally {
      context.hideLoader()
    }
  }

  // informações de preço
  const calcTotalPrice = () => {
    const { pricesData, formList } = state
    const scheduledPrice = formList.reduce(
      (total, form) => total + Number(form.scheduling?.price || 0),
      0
    )
    const schedulingPrice = pricesData.reduce(
      (total, price) => total + Number(price?.price || 0),
      0
    )
    return scheduledPrice + schedulingPrice
  }

  const calcDiscountedTotal = (currentSchedulePrice, currentPriceWithDiscount) => {
    const { formList, activeCampaign, schedulingCoupon } = state

    const scheduledPrice = formList.reduce(
      (total, form) => {
        const hasDiscount = form.data.activeCampaign || form.data.schedulingCoupon
        const price = hasDiscount ? form.scheduling.discountedPrice : form.scheduling.price
        return total + Number(price)
      },
      0
    )

    const hasActiveDiscount = activeCampaign || schedulingCoupon
    const schedulingPrice = hasActiveDiscount ? currentPriceWithDiscount : (currentSchedulePrice || 0)

    return scheduledPrice + schedulingPrice
  }

  const calcPriceWithoutPackageDiscount = (currentSchedulePrice) => {
    const packageService = state.pricesData?.find(price => price.additional === false)

    if (!packageService) {
      return 0
    }

    const discount = packageService?.packageDiscount
    const schedulePrice = currentSchedulePrice || 0

    if (typeof discount === 'number' && discount > 0 && discount < 100) {
      return (schedulePrice * 100) / (100 - discount)
    }

    return schedulePrice
  }

  const getActiveDiscount = () => {
    const { schedulingCoupon, activeCampaign } = state
    return schedulingCoupon ?? activeCampaign
  }

  const calculateCurrentSchedulePrice = () => {
    const { pricesData, scheduleBody } = state
    return calculateTotalPrice(
      pricesData,
      scheduleBody.isServicePackage,
      scheduleBody.additional_services
    )
  }

  const pricesInfo = () => {
    const activeDiscount = getActiveDiscount()
    const currentSchedulePrice = calculateCurrentSchedulePrice()

    const currentPriceWithDiscount = calcDiscountedPrice(activeDiscount, currentSchedulePrice)
    const discountedTotal = calcDiscountedTotal(currentSchedulePrice, currentPriceWithDiscount)
    const fullPrice = calcTotalPrice()
    const priceWithoutPackage = calcPriceWithoutPackageDiscount(currentSchedulePrice)
    const hasDiscount = currentSchedulePrice !== currentPriceWithDiscount

    return {
      currentSchedulePrice,
      currentPriceWithDiscount,
      totalPriceWithDiscount: discountedTotal,
      fullPrice,
      priceWithoutPackage: priceWithoutPackage || 0,
      currentHasDiscount: hasDiscount
    }
  }

  // remove o pet caso volte na tela inicial
  const handleUnselectPet = () => {
    const { scheduleBody } = state

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          pet_id: undefined,
          professional_id: ''
        },
        selectedPet: undefined
      }
    })
  }

  // busca e seleciona agendamento externo (minha agenda)
  const handleEditExternalScheduling = async (
    props,
    schedulingInfo,
    withoutDate = false,
    purchasedPackageData = undefined
  ) => {
    const { context } = props

    const isEditing = schedulingInfo.status === 'SCHEDULED'
    const isSchedulingFromPackage = !!schedulingInfo?.purchasedPackage

    let updatedState = {
      ...INITIAL_SCHEDULING_STATE
    }

    try {
      const petsList = (context.firstName && (await getAllMyPets())) || getPetsLocally()

      const selectedPet = petsList.find(({ id }) => id === schedulingInfo?.pet?.id)

      if (!selectedPet) {
        throw new Error('Não foi possível encontrar o PET do seu agendamento.')
      }

      if (!schedulingInfo?.service?.category) {
        throw new Error('Não foi possível encontrar a categoria do seu agendamento.')
      }

      const serviceList = await getServices(schedulingInfo?.service?.category)

      const selectedService = serviceList.find(({ id }) => id === schedulingInfo?.service?.id)

      if (!selectedService) {
        throw new Error('Não foi possível encontrar o serviço do seu agendamento.')
      }

      const formattedServiceList = serviceList.map(
        ({ id, name, additional, category }) => ({
          value: id,
          name,
          category,
          photo: undefined,
          additional
        })
      )

      const {
        additionalServices,
        mainServices
      } = splitServicesList(formattedServiceList)

      const additionalServicesNames = (schedulingInfo?.additionalServices || '')
        .split(', ')
        .filter((name) => name)

      let additionalServiceEntries = []

      if (
        Array.isArray(schedulingInfo?.schedulingAdditionalServices) &&
        schedulingInfo.schedulingAdditionalServices.length > 0
      ) {
        additionalServiceEntries = sumAdditionalServiceQuantitiesById(
          schedulingInfo.schedulingAdditionalServices
            .filter((item) => item?.id)
            .map((item) => ({
              id: item.id,
              quantity: parseSchedulingAdditionalQuantity(item)
            }))
        )
      } else if (additionalServicesNames.length > 0) {
        additionalServicesNames.forEach((name) => {
          const service = additionalServices?.find(
            ({ name: serviceName }) => serviceName === name
          )

          if (service) {
            additionalServiceEntries.push({
              id: service.value,
              quantity: 1
            })
          }
        })
      }

      let fetchedPackageData = purchasedPackageData
      let serviceIndex = schedulingInfo?.serviceIndex

      if (isSchedulingFromPackage && !fetchedPackageData) {
        try {
          const res = await getPurchasedPackageById({
            id: schedulingInfo.purchasedPackage
          })

          fetchedPackageData = res?.data
        } catch (e) {
          Sentry.captureException(e)
        }
      }

      if (isSchedulingFromPackage && !serviceIndex && schedulingInfo?.id) {
        try {
          const packageSchedulings = await getUserSchedulings({
            limit: 10,
            skip: 0,
            purchasedPackage: schedulingInfo.purchasedPackage,
            status: ['IN_PROGRESS', 'SCHEDULED', 'FINISHED', 'PAYMENT_PENDING'],
            includeOverbooks: true,
            concatResults: true
          })

          serviceIndex = resolvePackageServiceIndex(
            packageSchedulings?.schedulings,
            schedulingInfo.id
          )
        } catch (e) {
          Sentry.captureException(e)
        }
      }

      const selectedPurchasedPackage = isSchedulingFromPackage
        ? buildSelectedPurchasedPackage(fetchedPackageData)
        : undefined

      if (isSchedulingFromPackage && additionalServiceEntries.length > 0) {
        if (fetchedPackageData) {
          const allowedIds = filterAdditionalServiceIdsByPackageAvailability(
            additionalServiceEntries.map((e) => e.id),
            fetchedPackageData
          )

          const allowed = new Set(allowedIds)

          additionalServiceEntries = sumAdditionalServiceQuantitiesById(
            additionalServiceEntries.filter((e) => allowed.has(e.id))
          )
        } else {
          additionalServiceEntries = []
        }
      }

      const additionalServiceIds = additionalServiceEntries.flatMap(({ id, quantity }) =>
        quantity > 0 ? Array(quantity).fill(id) : []
      )

      const additionalServicesScheduleBody = additionalServiceEntries.map(({ id, quantity }) => ({
        id,
        quantity
      }))

      updatedState = {
        ...updatedState,
        activeCampaign: schedulingInfo?.campaign || undefined,
        petsData: petsList,
        serviceData: mainServices,
        additionalServiceData: additionalServices,
        scheduleBody: {
          additional_services: additionalServicesScheduleBody,
          pet_id: selectedPet?.id,
          service_id: schedulingInfo?.service?.id,
          purchasedPackageId: schedulingInfo?.purchasedPackage,
          isSchedulingFromPackage
        },
        selectedPet,
        activeStep: isSchedulingFromPackage
          ? PURCHASED_PACKAGE_SCHEDULING_STEPS.PET_AND_SERVICE
          : SCHEDULING_STEPS.PARTNER
      }

      const partnerSelected = await getPartnerById(schedulingInfo.partnerId)

      if (!partnerSelected?.id) {
        throw new Error('Não foi possível encontrar a loja do seu agendamento.')
      }

      const daySelected = moment(schedulingInfo?.day?.split('-'), 'DD/MM/YY')

      // Caso seja edição de um agendamento, considera o mês e ano do agendamento.
      // Caso seja repetição de um agendamento, considera o mês e ano atuais.
      const currentMonth = isEditing ? daySelected.month() : moment().month()
      const currentYear = isEditing ? daySelected.year() : moment().year()

      const availableSlots = await calculateAvailabilities({
        partnerId: partnerSelected?.id,
        serviceId: schedulingInfo?.service?.id,
        additionalServices: additionalServiceIds,
        breedId: schedulingInfo?.pet?.breed?.id,
        temperament: schedulingInfo?.pet?.temperament,
        month: currentMonth + 1,
        year: currentYear
      })

      const professionalIds = Object.keys(availableSlots).filter(
        (professionalId) => professionalId !== ''
      )

      const professionalList = await getNames(professionalIds)

      const professionalSelected = professionalList?.find(
        ({ id }) => id === schedulingInfo.professionalId
      )

      const partnerEnablePayment = partnerSelected?.enabledPayment === true
      const partnerOpenSeller = partnerSelected?.openSeller === true
      const professionalEnablePayment = professionalSelected?.enabledPayment === true

      const havePayment = partnerEnablePayment &&
        (professionalEnablePayment || partnerOpenSeller)

      const professionalData = professionalList
        .map(({ id, name, photo, enabledPayment, ratingAverage }) => ({
          id,
          name,
          photo,
          enabledPayment: enabledPayment || false,
          ratingAverage
        }))
        .filter(({ enabledPayment }) =>
          (havePayment && enabledPayment === havePayment) || !havePayment
        )

      let unavailabilityPets = updatedState?.error?.unavailabilityPets || []

      if (!professionalData.length) {
        const unavailablePetSchedule = petsList.find(
          (pet) => selectedPet && pet.id === selectedPet?.id
        )

        if (unavailablePetSchedule) {
          unavailabilityPets = [
            ...unavailabilityPets,
            {
              ...unavailablePetSchedule
            }
          ]
        }
      }

      const hasAvailableProfessional = professionalData?.some(({ id }) => id === professionalSelected?.id)

      const professionalIsSelectable = !!professionalSelected?.id && !!hasAvailableProfessional

      if (isSchedulingFromPackage && !professionalIsSelectable) {
        return {
          ...updatedState,
          reeschedulingError: true,
          reeschedulingErrorReason: 'PACKAGE_PROFESSIONAL_UNAVAILABLE',
          unavailableProfessional: {
            id: schedulingInfo?.professionalId,
            name: schedulingInfo?.professionalName
          },
          activeStep: 4,
          scheduleBody: {
            ...updatedState.scheduleBody,
            professional_id: '',
            selectDay: '',
            selectTime: '',
            purchasedPackageId: schedulingInfo?.purchasedPackage,
            isSchedulingFromPackage: true
          },
          professionalData: professionalList,
          timeData: [],
          times: [],
          review: false,
          isHowToPay: false,
          error: {
            ...updatedState.error,
            professional_id: 'O profissional do agendamento original não está mais disponível. Selecione outro profissional.',
            selectDay: 'Selecione um dia para agendamento',
            selectTime: 'Selecione um horário para atendimento',
            unavailabilityPets
          }
        }
      }

      updatedState = {
        ...updatedState,
        professionalData,
        activeStep: 4
      }

      const calendarDate = {
        month: currentMonth + 1,
        year: currentYear
      }

      const services = [
        ...additionalServiceIds,
        schedulingInfo?.service?.id
      ]

      const productCoupon = await handleFetchCoupon()

      const [campaignList, price] = await Promise.all([
        isSchedulingFromPackage
          ? Promise.resolve([])
          : handleFetchCampaigns(
            calendarDate,
            partnerSelected?.id,
            schedulingInfo?.service?.id
          ),
        getPrice({
          partnerId: partnerSelected?.id,
          breedId: schedulingInfo?.pet?.breed?.id,
          services
        })
      ])

      const pricesData = price
        ? price.map((priceData) => ({
          service: priceData.service.name,
          price: priceData.price_value,
          service_time: priceData.service_time,
          additional: priceData.service.additional
        }))
        : []

      updatedState = {
        ...updatedState,
        scheduleBody: {
          ...updatedState?.scheduleBody,
          partner_id: partnerSelected?.id,
          professional_id: '',
          purchasedPackageId: schedulingInfo?.purchasedPackage,
          isSchedulingFromPackage
        },
        isCustomAddress: true,
        customAddressType: 'storeName',
        storeName: schedulingInfo.partnerFantasyName,
        totalPartners: 1,
        partnersData: [partnerSelected],
        calendarMonth: currentMonth + 1,
        calendarYear: currentYear,
        timeData: [],
        campaigns: campaignList,
        pricesData,
        availableSlots,
        changeStore: false,
        usingGeolocation: false,
        error: {
          locations: 'Informe sua localização',
          professional_id: 'Selecione um profissional para o atendimento',
          selectDay: 'Selecione um dia para agendamento',
          selectTime: 'Selecione um horário para atendimento',
          unavailabilityPets
        }
      }

      if (!professionalSelected?.id) {
        throw new Error('Não foi possível encontrar o profissional do seu agendamento.')
      }

      const professionalSlots = availableSlots?.[professionalSelected.id] || {}
      const dayKey = daySelected.format('D')

      // Se for edição, re-adiciona o horário marcado no quadro de horários disponíveis.
      if (!withoutDate) {
        professionalSlots[dayKey] = professionalSlots[dayKey] || []
        professionalSlots[dayKey].push(schedulingInfo?.time)
      }

      let availableDates = Object.keys(professionalSlots).map((day) => parseInt(day))

      if (isSchedulingFromPackage && fetchedPackageData) {
        const allowedPackageWeekdays = fetchedPackageData.service?.packageWeekdays
        const expiresAt = fetchedPackageData.expiresAt

        if (allowedPackageWeekdays?.length) {
          availableDates = availableDates.filter((day) => {
            const weekday = moment({
              year: currentYear,
              month: currentMonth,
              day
            }).day()

            return allowedPackageWeekdays.includes(weekday)
          })
        }

        if (expiresAt) {
          const expiresAtDate = moment(expiresAt)

          availableDates = availableDates.filter((day) => {
            const date = moment({
              year: currentYear,
              month: currentMonth,
              day
            })

            return date.isSameOrBefore(expiresAtDate, 'day')
          })
        }
      }

      const selectedDaySlots = professionalSlots?.[dayKey] || []

      updatedState = {
        ...updatedState,
        availableDates
      }

      /**
       * Payload para parar no fluxo de seleção de data/hora.
       */
      if (withoutDate) {
        updatedState = {
          ...updatedState,
          scheduleBody: {
            ...updatedState.scheduleBody,
            professional_id: professionalSelected?.id,
            selectDay: '',
            selectTime: '',
            purchasedPackageId: schedulingInfo?.purchasedPackage,
            isSchedulingFromPackage,
            serviceIndex
          },
          professionalData,
          timeData: selectedDaySlots,
          times: selectedDaySlots,
          invertStep: false,
          obs: schedulingInfo?.obs,
          activeStep: isSchedulingFromPackage
            ? PURCHASED_PACKAGE_SCHEDULING_STEPS.DATE_AND_TIME
            : SCHEDULING_STEPS.DATE_AND_TIME,
          review: false,
          isHowToPay: false,
          productCoupon,
          ...(selectedPurchasedPackage ? { selectedPurchasedPackage } : {}),
          error: {
            selectDay: 'Selecione um dia para agendamento',
            selectTime: 'Selecione um horário para atendimento',
            unavailabilityPets: []
          },
          schedulingData: {
            pet: selectedPet,
            service: schedulingInfo?.service,
            partner: partnerSelected,
            professional: professionalSelected
          }
        }

        return updatedState
      }

      updatedState = {
        ...updatedState,
        scheduleBody: {
          ...updatedState.scheduleBody,
          professional_id: professionalSelected?.id,
          selectDay: daySelected.format('YYYY-MM-DD'),
          selectTime: schedulingInfo?.time,
          purchasedPackageId: schedulingInfo?.purchasedPackage,
          isSchedulingFromPackage,
          serviceIndex
        },
        professionalData,
        timeData: selectedDaySlots,
        times: selectedDaySlots,
        schedulingCoupon: schedulingInfo?.schedulingCoupon,
        invertStep: false,
        obs: schedulingInfo?.obs,
        activeStep: isSchedulingFromPackage
          ? PURCHASED_PACKAGE_SCHEDULING_STEPS.REVIEW
          : SCHEDULING_STEPS.REVIEW,
        review: true,
        productCoupon,
        ...(selectedPurchasedPackage ? { selectedPurchasedPackage } : {}),
        error: {
          unavailabilityPets: []
        },
        schedulingData: {
          pet: selectedPet,
          service: schedulingInfo?.service,
          partner: partnerSelected,
          professional: professionalSelected,
          selectDay: daySelected.format('YYYY-MM-DD'),
          selectTime: schedulingInfo?.time,
          hasOnlinePayment: schedulingInfo.hasOnlinePayment,
          schedulingCoupon: schedulingInfo?.schedulingCoupon
        },
        isEditingScheduling: true
      }

      return updatedState
    } catch (err) {
      return {
        ...updatedState,
        reeschedulingError: true
      }
    }
  }

  // aceita termo de uso
  const handleAcceptSchedulingTerms = () => {
    const { useTerms } = state

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        useTerms: !useTerms
      }
    })
  }

  // aceita termo de adesão ao pacote
  const handleAcceptPackageUseTerms = () => {
    const { packageUseTerms } = state

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        packageUseTerms: !packageUseTerms
      }
    })
  }

  const handleUnselectPurchasedPackage = () => {
    if (!state.selectedPurchasedPackage) return

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: INITIAL_SCHEDULE_BODY,
        selectedPurchasedPackage: {}
      }
    })
  }

  const baseSteps = [
    {
      step: SCHEDULING_STEPS.PET_AND_SERVICE,
      url: 'pet-e-servico',
      label: 'Pet + serviço',
      disabledRule: !state.scheduleBody.service_id || state.scheduleBody.pet_id === undefined,
      path: '/pet-e-servico',
      id: SCHEDULING_STEPS.PET_AND_SERVICE
    },
    {
      step: SCHEDULING_STEPS.PARTNER,
      url: 'loja',
      label: 'Local',
      disabledRule: (!state.scheduleBody.partner_id || (state.error.unavailabilityPets.length > 0 && state.scheduleBody.samePetDate) || !state.partnersData.length),
      nextHiddenRule: !state.scheduleBody.partner_id,
      active: state.scheduleBody?.partner_id && !!Object.keys(state?.availableSlots || {}).length,
      path: '/loja',
      id: SCHEDULING_STEPS.PARTNER
    }
  ]

  const servicePackageSteps = [
    ...baseSteps,
    {
      step: SERVICE_PACKAGE_STEPS.PROFESSIONAL,
      url: 'profissional',
      label: 'Profissional',
      disabledRule: (!state.scheduleBody.professional_id && !state.noPreference),
      active: state.scheduleBody?.professional_id || state.noPreference,
      path: '/profissional',
      id: SERVICE_PACKAGE_STEPS.PROFESSIONAL
    },
    {
      step: SERVICE_PACKAGE_STEPS.PAYMENT_ADDRESS,
      url: 'endereco',
      label: 'Endereço',
      path: '/checkout/endereco',
      active: false,
      id: SERVICE_PACKAGE_STEPS.PAYMENT_ADDRESS
    },
    {
      step: SERVICE_PACKAGE_STEPS.HOW_TO_PAY,
      url: 'forma-de-pagamento',
      label: 'Forma de Pagamento',
      path: '/checkout/forma-de-pagamento',
      active: false,
      id: SERVICE_PACKAGE_STEPS.HOW_TO_PAY
    }
  ]

  const generateSteps = () => {
    if (state?.scheduleBody.isSchedulingFromPackage) {
      return [
        {
          step: PURCHASED_PACKAGE_SCHEDULING_STEPS.PET_AND_SERVICE,
          url: 'pet-e-servico',
          label: 'Pet + serviço',
          disabledRule: !state.selectedPurchasedPackage || (typeof state.selectedPurchasedPackage === 'object' && Object.keys(state.selectedPurchasedPackage).length === 0),
          path: '/pet-e-servico',
          id: PURCHASED_PACKAGE_SCHEDULING_STEPS.PET_AND_SERVICE
        },
        {
          step: PURCHASED_PACKAGE_SCHEDULING_STEPS.DATE_AND_TIME,
          url: 'data',
          label: 'Data e horário',
          disabledRule: !state.scheduleBody.selectTime,
          active: state.scheduleBody?.selectTime,
          path: '/data',
          id: PURCHASED_PACKAGE_SCHEDULING_STEPS.DATE_AND_TIME
        },
        {
          step: PURCHASED_PACKAGE_SCHEDULING_STEPS.REVIEW,
          url: 'revisao-do-agendamento',
          label: 'Revise seu agendamento',
          disabledRule: (
            !state.scheduleBody.partner_id ||
            !state.scheduleBody.professional_id ||
            !state.scheduleBody.service_id ||
            state.scheduleBody.pet_id === undefined ||
            !state.scheduleBody.selectDay ||
            !state.scheduleBody.selectTime
          ),
          path: '/revisao-do-agendamento',
          id: PURCHASED_PACKAGE_SCHEDULING_STEPS.REVIEW
        }
      ]
    }

    if (state?.scheduleBody.isServicePackage) {
      return servicePackageSteps
    }

    return [
      ...baseSteps,
      {
        step: SCHEDULING_STEPS.FLOW_SELECTION,
        url: 'selecao-de-fluxo',
        label: 'Seleção de fluxo',
        disabledRule: state.invertStep === undefined,
        active: state.invertStep !== undefined,
        path: '/selecao-de-fluxo',
        id: SCHEDULING_STEPS.FLOW_SELECTION
      },
      {
        step: handleShouldInvertSteps() ? SCHEDULING_STEPS.DATE_AND_TIME : SCHEDULING_STEPS.PROFESSIONAL,
        url: 'profissional',
        label: 'Profissional',
        disabledRule: (!state.scheduleBody.professional_id && !state.noPreference),
        active: state.scheduleBody?.professional_id || state.noPreference,
        path: '/profissional',
        id: handleShouldInvertSteps() ? SCHEDULING_STEPS.DATE_AND_TIME : SCHEDULING_STEPS.PROFESSIONAL
      },
      {
        step: handleShouldInvertSteps() ? SCHEDULING_STEPS.PROFESSIONAL : SCHEDULING_STEPS.DATE_AND_TIME,
        url: 'data',
        label: 'Data e horário',
        disabledRule: !state.scheduleBody.selectTime,
        active: state.invertStep ? (state?.updatedProfessionals && state.scheduleBody?.selectTime) : state.scheduleBody?.selectTime,
        path: '/data',
        id: handleShouldInvertSteps() ? SCHEDULING_STEPS.PROFESSIONAL : SCHEDULING_STEPS.DATE_AND_TIME
      },
      {
        step: SCHEDULING_STEPS.REVIEW,
        url: 'revisao-do-agendamento',
        label: 'Revise seu agendamento',
        disabledRule: (!state.scheduleBody.partner_id || !state.scheduleBody.professional_id || !state.scheduleBody.service_id || state.scheduleBody.pet_id === undefined || !state.scheduleBody.selectDay || !state.scheduleBody.selectTime),
        path: '/revisao-do-agendamento',
        id: SCHEDULING_STEPS.REVIEW
      },
      {
        step: SCHEDULING_STEPS.HOW_TO_PAY,
        url: 'como-pagar',
        label: 'Escolha como pagar',
        disabledRule: (!state.scheduleBody.partner_id || !state.scheduleBody.professional_id || !state.scheduleBody.service_id || state.scheduleBody.pet_id === undefined || !state.scheduleBody.selectDay || !state.scheduleBody.selectTime || !state.scheduleBody.paymentType),
        path: '/como-pagar',
        id: SCHEDULING_STEPS.HOW_TO_PAY
      }
    ]
  }

  const steps = generateSteps()

  // retorna estado para o momento de cada step (apenas durante o fluxo por exemplos: tela de resumo e botão voltar)
  const generateEditSteps = (state) => {
    const baseSteps = {
      0: {
        ...INITIAL_SCHEDULING_STATE,
        serviceData: state.serviceData,
        additionalServiceData: state.additionalServiceData,
        obs: state.obs
      },
      1: {
        ...INITIAL_SCHEDULING_STATE,
        scheduleBody: {
          ...INITIAL_SCHEDULING_STATE.scheduleBody,
          pet_id: state.selectedPet?.id,
          service_id: state.scheduleBody.service_id,
          partner_id: state.scheduleBody?.isSchedulingFromPackage ? state.scheduleBody.partner_id : '',
          professional_id: state.scheduleBody?.isSchedulingFromPackage ? state.scheduleBody.professional_id : '',
          additional_services: state.scheduleBody.additional_services,
          isServicePackage: state.scheduleBody?.isServicePackage,
          purchasedPackageId: state.scheduleBody?.purchasedPackageId,
          isSchedulingFromPackage: state.scheduleBody?.isSchedulingFromPackage
        },
        serviceData: state.serviceData,
        additionalServiceData: state.additionalServiceData,
        selectedPet: state.selectedPet,
        formList: state.formList,
        petsData: state.petsData,
        obs: state.obs,
        isAllAdditionalServicesSelected: state.isAllAdditionalServicesSelected,
        activeStep: SCHEDULING_STEPS.PET_AND_SERVICE,
        error: {
          ...INITIAL_SCHEDULING_STATE?.error,
          pet_id: undefined,
          unavailabilityPets: [],
          service_id: undefined
        }
      },
      2: {
        ...INITIAL_SCHEDULING_STATE,
        pricesData: state.pricesData ? state.pricesData : [],
        changeStore: state.changeStore,
        storeName: state.storeName,
        usingGeolocation: state.usingGeolocation,
        isCustomAddress: state.isCustomAddress,
        customAddressType: state.customAddressType,
        address: state.address,
        productCoupon: state.productCoupon,
        campaigns: state.campaigns,
        scheduleBody: {
          ...INITIAL_SCHEDULING_STATE.scheduleBody,
          pet_id: state.selectedPet?.id,
          service_id: state.scheduleBody.service_id,
          additional_services: state.scheduleBody?.additional_services || [],
          partner_id: state.scheduleBody.partner_id,
          professional_id: state.scheduleBody?.isSchedulingFromPackage ? state.scheduleBody.professional_id : '',
          locations: state.scheduleBody.locations,
          samePetDate: state.scheduleBody?.samePetDate,
          isServicePackage: state.scheduleBody?.isServicePackage,
          purchasedPackageId: state.scheduleBody?.purchasedPackageId,
          isSchedulingFromPackage: state.scheduleBody?.isSchedulingFromPackage
        },
        serviceData: state.serviceData,
        professionalData: (state.scheduleBody?.isServicePackage || state.scheduleBody?.isSchedulingFromPackage) ? state.professionalData : [],
        additionalServiceData: state.additionalServiceData,
        selectedPet: state.selectedPet,
        selectedPetSchedule: state?.selectedPetSchedule,
        petsData: state.petsData,
        formList: state.formList,
        partnersData: state.partnersData,
        availableDates: state.availableDates,
        availableSlots: state.availableSlots,
        activeStep: SCHEDULING_STEPS.PARTNER,
        error: {
          ...INITIAL_SCHEDULING_STATE?.error,
          pet_id: undefined,
          unavailabilityPets: [],
          service_id: undefined,
          partner_id: undefined,
          professional_id: state.scheduleBody?.isSchedulingFromPackage ? undefined : ''
        },
        obs: state.obs
      }
    }

    if (state?.scheduleBody.isSchedulingFromPackage) {
      return {
        ...baseSteps,
        2: {
          ...state,
          calendarMonth: state.calendarMonth,
          calendarYear: state.calendarYear,
          activeStep: PURCHASED_PACKAGE_SCHEDULING_STEPS.DATE_AND_TIME,
          obs: state.obs,
          review: false,
          isHowToPay: false
        }
      }
    }

    if (state?.scheduleBody.isServicePackage) {
      return {
        ...baseSteps,
        3: {
          ...state,
          scheduleBody: {
            ...state.scheduleBody,
            selectDay: state.scheduleBody?.samePetDate ? state.scheduleBody.selectDay : '',
            selectTime: state.scheduleBody?.samePetDate ? state.scheduleBody.selectTime : '',
            professional_id: ''
          },
          activeStep: SERVICE_PACKAGE_STEPS.PROFESSIONAL,
          obs: state.obs,
          noPreference: false
        }
      }
    }

    if (!state?.scheduleBody.isServicePackage) {
      return {
        ...baseSteps,
        3: {
          ...state,
          calendarMonth: state.calendarMonth,
          calendarYear: state.calendarYear,
          productCoupon: state.productCoupon,
          campaigns: state.campaigns,
          scheduleBody: {
            ...state.scheduleBody,
            selectDay: '',
            selectTime: '',
            professional_id: state.scheduleBody?.isSchedulingFromPackage ? state.scheduleBody.professional_id : '',
            isServicePackage: state.scheduleBody?.isServicePackage,
            purchasedPackageId: state.scheduleBody?.purchasedPackageId,
            isSchedulingFromPackage: state.scheduleBody?.isSchedulingFromPackage
          },
          activeStep: SCHEDULING_STEPS.FLOW_SELECTION,
          obs: state.obs
        },
        4: {
          ...state,
          calendarMonth: state.calendarMonth,
          calendarYear: state.calendarYear,
          productCoupon: state.productCoupon,
          campaigns: state.campaigns,
          scheduleBody: {
            ...state.scheduleBody,
            ...(handleShouldInvertSteps() ? {
              professional_id: state.scheduleBody?.isSchedulingFromPackage ? state.scheduleBody.professional_id : ''
            } : {
              selectDay: '',
              selectTime: ''
            })
          },
          activeStep: SCHEDULING_STEPS.PROFESSIONAL,
          noPreference: false,
          obs: state.obs
        },
        5: {
          ...state,
          activeStep: SCHEDULING_STEPS.DATE_AND_TIME,
          obs: state.obs,
          review: true,
          isHowToPay: false,
          finalStep: true,
          scheduleBody: {
            ...state.scheduleBody,
            paymentType: undefined
          }
        },
        6: {
          ...state,
          activeStep: SCHEDULING_STEPS.REVIEW,
          obs: state.obs,
          review: true,
          isHowToPay: false
        }
      }
    }

    return baseSteps
  }

  const editSteps = (stateParam) => generateEditSteps(stateParam || state)

  // exporta as funções
  const functions = {
    showSchedulingFailureModal,
    handleSetRescheduleDataLoaded,
    checkReservedDate,
    hasSchedulingChanged,
    handleEditPetScheduling,
    handleShouldInvertSteps,
    handleAddPetToScheduling,
    handleUnselectPet,
    handleUnselectPurchasedPackage,
    handleConfirmScheduling,
    handlePackagePurchase,
    handleWarningModalClose,
    handleFetchCampaigns,
    handleFetchCoupon,
    pricesInfo,
    findPartnerScheduleAndCampaignAviability,
    filterDatesByPackageExpiry,
    handleEditExternalScheduling,
    handleEditMultipleScheduling,
    addCouponToSchedule,
    removeCouponFromSchedule,
    handleAcceptSchedulingTerms,
    handleAcceptPackageUseTerms
  }

  // Salva o estado no localStorage sempre que ele for atualizado
  useEffect(() => {
    saveStateToLocalStorage(state)
  }, [state])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const urlParams = new URLSearchParams(window.location.search)
    const isResetQuery = router.query?.resetScheduling === 'true' || urlParams.get('resetScheduling') === 'true'

    if (isResetQuery) {
      window.localStorage.removeItem('schedulingState')
      window.localStorage.removeItem('paymentState')
      window.localStorage.removeItem('category')

      dispatch({ type: actionTypes.RESET_STATE })

      const currentPath = router.pathname || window.location.pathname
      const firstStepPath = PAGE_URL.SCHEDULING_PET_AND_SERVICES

      const { resetScheduling, ...cleanQuery } = router.query || {}

      if (!currentPath.includes(firstStepPath)) {
        router.replace({
          pathname: firstStepPath,
          query: cleanQuery
        })
      } else if (router.query?.resetScheduling) {
        router.replace(
          {
            pathname: currentPath,
            query: cleanQuery
          },
          undefined,
          { shallow: true }
        )
      }
    }
  }, [router.query?.resetScheduling, router.pathname])

  return (
    <serviceScheduleContext.Provider value={{ state, dispatch, functions, steps, editSteps, servicePackageSteps }}>
      <PetAndServicesContextProvider>
        <LocationContextProvider>
          <ProfessionalContextProvider>
            <DateTimeContextProvider>
              {children}
            </DateTimeContextProvider>
          </ProfessionalContextProvider>
        </LocationContextProvider>
      </PetAndServicesContextProvider>
    </serviceScheduleContext.Provider>
  )
}

// Hook para acessar o contexto
export function useServiceScheduleContext () {
  const context = useContext(serviceScheduleContext)
  if (!context) {
    throw new Error('useServiceScheduleContext deve ser usado dentro de um Provider!')
  }
  const { state, dispatch, functions, steps, editSteps, servicePackageSteps } = context
  return { state, dispatch, functions, steps, editSteps, servicePackageSteps }
}
